import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Backdrop,
  Fade,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { LoadingButton } from "@mui/lab";
import { useState, useEffect } from "react";

const RemoteConnectionModal = ({
  remoteModalOpen,
  closeRemoteModal,
  customerSelectedRow,
  remoteCredentials,
  setRemoteCredentials,
  handleRemoteConnect,
  connectionLoading,
  connectionMsg,
  connectionMsgIsSuccess,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    if (!remoteModalOpen) {
      setShowPassword(false);
    }
  }, [remoteModalOpen]);

  return (
    <Modal
      aria-labelledby="remote-connection-modal-title"
      aria-describedby="remote-connection-modal-description"
      open={remoteModalOpen}
      onClose={connectionLoading ? () => {} : closeRemoteModal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 300 } }}
    >
      <Fade in={remoteModalOpen}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 520,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Box>
              <Typography
                id="remote-connection-modal-title"
                variant="h5"
                fontWeight="bold"
                align="center"
                color="primary"
              >
                Remote Connection Into Account
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ mt: 0, fontWeight: "bold" }}
              >
                {customerSelectedRow?.customerName}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ mt: -0.8, fontWeight: "bold" }}
              >
                {customerSelectedRow?.email}
              </Typography>
            </Box>
            <IconButton
              disabled={connectionLoading}
              aria-label="Close remote connection modal"
              onClick={closeRemoteModal}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography
            id="remote-connection-modal-description"
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Enter the credentials below to make a remote connection into the
            selected user's account.
          </Typography>

          <TextField
            fullWidth
            variant="filled"
            label="Username"
            sx={{ mb: 2 }}
            value={remoteCredentials.username}
            error={remoteCredentials.usernameError}
            helperText={
              remoteCredentials.usernameError ? "Username is required" : ""
            }
            onChange={(e) =>
              setRemoteCredentials((prev) => ({
                ...prev,
                username: e.target.value,
                usernameError: false,
              }))
            }
          />

          <TextField
            fullWidth
            variant="filled"
            type={showPassword ? "text" : "password"}
            label="Password"
            value={remoteCredentials.password}
            error={remoteCredentials.passwordError}
            helperText={
              remoteCredentials.passwordError ? "Password is required" : ""
            }
            onChange={(e) =>
              setRemoteCredentials((prev) => ({
                ...prev,
                password: e.target.value,
                passwordError: false,
              }))
            }
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {connectionMsg && (
            <Typography
              color={connectionMsgIsSuccess ? "success.main" : "error"}
              fontWeight="bold"
              variant="body2"
              align="center"
              sx={{ mb: 2, mt: 1 }}
            >
              {connectionMsg}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              disabled={connectionLoading}
              variant="outlined"
              onClick={closeRemoteModal}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="success"
              loading={connectionLoading}
              onClick={handleRemoteConnect}
            >
              Connect to Account
            </LoadingButton>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default RemoteConnectionModal;
